import type Qty              from "@/core/types/Qty";
import type Demand           from "@/domains/Demand/Demand";
import type Tender           from "@/domains/Tenders/Tender";
import type TenderBid        from "@/domains/TenderBids/TenderBid";
import type DemandOption     from "@/domains/DemandsV2/DemandOption";
import type DemandV2         from "@/domains/DemandsV2/DemandV2";
import type DemandRequest    from "@/domains/DemandsV2/DemandRequest";

import Title_ru_RU_Class     from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU           from "@/core/misc/decorators/Title_ru_RU";
import TYPES                 from "@/core/services/Changelogs/types";
import Core                  from '@/core/Core';
import TimeStamp             from "@/core/types/TimeStamp";
import Sum                   from "@/core/types/Sum";
import AbstractOrderEntity   from '@/domains/Abstract/AbstractOrderEntity';
import OrderRequisites       from '@/domains/Orders/OrderRequisites';
import OrderPosition         from '@/domains/Orders/OrderPosition';
import PreorderPosition from "@/domains/Preorders/PreorderPosition";
import AbstractOrderPosition from '@/domains/Abstract/AbstractOrderPosition';
import OrderCategory         from "@/domains/Orders/OrderCategory";
import Currency              from "@/domains/Currencies/Currency";
import CurrenciesService     from "@/domains/Currencies/CurrenciesService";
import Offer                 from "@/domains/Offers/Offer";

@Title_ru_RU_Class('Заказ')
class Order extends AbstractOrderEntity {

	static Events = {
		Created: 'order:created',
		Updated: 'order:updated',
		Deleted: 'order:deleted',
    NotificationSent: 'order:notificationSent',
	} as const;

    static REASON_UNAVAILABLE = {
        NO_EXIST_ENTITY_ON_BACKEND: 'Заказ не сохранён',
        NO_POSITIONS:               'Заказ не содержит позиций',
        ORDER_ALREADY_APPROVED:     'Заказ уже подтверждён',
    } as const;

    /** Является черновиком */
    @Title_ru_RU('метка черновика', TYPES.BOOLEAN, AbstractOrderEntity.isDraftParser)
    public isDraft: boolean;

    // Дата отправки уведомления
    public notificationSentAt: TimeStamp | null;

    /* Минимальная ЖДД по заказу, используется в уведомлениях */
  // todo надо слазить на бек, и если на беке этого поля нет, то выбросить
    // public minimalDesiredReceiptDate: DateSimple;

    /** Комментарий */
    @Title_ru_RU('комментарий поставщику', TYPES.STRING)
    public notes: string;

    /** Копия другого заказа. Живёт только на фронте — бейдж в карточке. */
    public isCopy: boolean;


    public positions: AbstractOrderPosition[] = [];

    static fromServer(data: any, result = new Order()): Order {
        super.fromServer(data, result);

        result.notificationSentAt = TimeStamp.fromServer(data.notificationSentAt);

        result.positions = Core.utils.hydrateArray(data.positions, (positionData) => OrderPosition.fromServer(positionData));

        // Привязка позиций к заказу: общий requisites по ссылке + обратная ссылка.
        // Заказ — хозяин связи заказ↔позиция, поэтому привязка здесь, а не в позиции.
        result.positions.forEach((position) => {
            position.requisites = result.requisites;
            position.order      = result;
        });

        return result;
    }

    static fromNew(orderCategory: OrderCategory, result = new Order()): Order {
        result.requisites = OrderRequisites.fromNew(orderCategory);
        result.isDraft    = false;
        // Инициализирую, для того чтобы считать accounting блок
        result.createdAt = new TimeStamp();

        return result;
    }

    static fromOffer(offer: Offer, demand: Demand | null = null, result = new Order()): Order {
        super.fromOffer(offer, result);

        result.isDraft   = false;
        // Инициализирую, для того чтобы считать accounting блок
        result.createdAt = new TimeStamp();

        result.addPosition(this.createPositionFromOffer(offer, demand));

        return result;
    }

    /**
     * Какого класса позицию создаёт фабрика из предложения.
     * Вынесено отдельно, чтобы Preorder подставил PreorderPosition, не
     * переписывая всю фабрику.
     */
    protected static createPositionFromOffer(offer: Offer, demand: Demand | null): AbstractOrderPosition {
        return OrderPosition.fromOffer(offer, demand);
    }

    static fromTenderBid(tenderBid: TenderBid, tender: Tender, result = new Order()): Order {
        super.fromTenderBid(tenderBid, tender, result);

        result.isDraft   = false;
        result.createdAt = new TimeStamp();

        let position;
        if(result.isPreorder === true) {
          position = PreorderPosition.fromTenderBid(tenderBid, tender);
        } else {
          position = OrderPosition.fromTenderBid(tenderBid, tender);
        }

        result.addPosition(position);

        return result;
    }

    static fromDemandV2Option(usedOption: { option: DemandOption, usedQty: Qty }, result = new Order()): Order {
        super.fromDemandV2Option(usedOption, result);

        result.isDraft   = false;
        result.createdAt = new TimeStamp();

        return result;
    }

    /**
     * Заказы из потребности V2.
     *
     * Каждая заявка удовлетворяется опциями; опции одного поставщика внутри
     * одной заявки складываются в ОДИН заказ этому поставщику. Отсюда двойной
     * цикл: заявки → группы по поставщику.
     *
     * Распределение читается один раз: DemandV2.distribution — геттер, он
     * пересчитывает всю потребность на каждое чтение, а request.usedOptions
     * читает его же. Проход по заявкам с обращением к request.usedOptions
     * пересчитывал бы распределение столько раз, сколько заявок.
     */
    static fromDemandV2(demandV2: DemandV2): Order[] {
        const result: Order[] = [];

        for (const [request, usedOptions] of demandV2.distribution) {
            const groupedBySeller = demandV2.aggregateUsedOptionsBySeller(usedOptions);

            for (const sellerGroup of groupedBySeller) {
                result.push(Order.fromDemandV2UsedOptions(request, sellerGroup));
            }
        }

        return result;
    }

    /**
     * Заказ одному поставщику по одной заявке.
     * Реквизиты и валюта — из любой опции группы: поставщик у них общий.
     * Позиций столько, сколько опций в группе.
     */
    static fromDemandV2UsedOptions(
        request: DemandRequest,
        usedOptions: Array<{ option: DemandOption, usedQty: Qty }>,
        result = new Order(),
    ): Order {
        super.fromDemandV2Option(usedOptions[0], result);

        result.isDraft   = false;
        result.createdAt = new TimeStamp();

        for (const usedOption of usedOptions) {
            result.addPosition(OrderPosition.fromDemandV2Option(request, usedOption));
        }

        return result;
    }

    static fromPreorderPosition(preorderPosition: PreorderPosition, result = new Order()): Order {
        super.fromPreorderPosition(preorderPosition, result);

        result.isDraft   = false;
        result.createdAt = new TimeStamp();

        result.addPosition(this.createPositionFromPreorderPosition(preorderPosition));

        return result;
    }

    /** Какого класса позицию создаёт фабрика из позиции предзаказа. */
    protected static createPositionFromPreorderPosition(preorderPosition: PreorderPosition): AbstractOrderPosition {
        return OrderPosition.fromPreorderPosition(preorderPosition);
    }

    /**
     * Копия существующего заказа — новый черновик на бэк.
     * Идентичность сбрасывается и у заказа, и у каждой позиции: иначе копия
     * уйдёт на сохранение под чужими id и перезапишет исходный заказ.
     * Ответственный — тот, кто копирует, а не тот, кто создавал оригинал.
     */
    static fromAnotherOrder(order: Order, result = Core.utils.cloneDeep(order)): Order {
        result.isCopy  = true;
        result.isDraft = true;
        result.resetIdentity();

        result.responsibleBuyer = Core.currentUser;

        result.positions.forEach((position) => {
            position.resetIdentity();
            position.isDraft = true;
        });

        return result;
    }

    // ==================== Работа с позициями ====================

    /**
     * Добавляет позицию (или несколько) в заказ.
     * Позиция получает ССЫЛКУ на requisites заказа — её собственные реквизиты
     * (если пришла из истории/импорта/оффера) отбрасываются. Это гарантирует
     * единство реквизитов заказа и всех его позиций.
     */
    addPosition(position: AbstractOrderPosition): void;
    addPosition(positions: AbstractOrderPosition[]): void;
    addPosition(positionOrList: AbstractOrderPosition | AbstractOrderPosition[]): void {
      if (Array.isArray(positionOrList)) {
        positionOrList.forEach((position) => {
          this.addPosition(position);
        });
      } else {
        const position = positionOrList;

        // Позиция переходит на реквизиты заказа: собственные, полученные от
        // фабрики источника, отбрасываются. Заказ — хозяин связи.
        position.setOrder(this);

        // Валюта заказа выводится из позиций: первая позиция её задаёт,
        // следующая с другой валютой делает заказ мультивалютным — валюта заказа зануляется.
        // Сравнение идёт после setOrder: до него валюта позиции может быть ещё не проставлена.
        if (Core.utils.isNullOrUndefined(this._currencyCode) === true) {
          this.currencyCode = position.currencyCode;
        } else {
          if (this.currencyCode !== position.currencyCode) {
            this.currency = null;
          } else {
            // валюта совпала — заказ остаётся одновалютным
          }
        }

        this.positions.push(position);
      }
    }

    /**
     * Удаляет позицию из заказа.
     *
     * Сначала по ссылке: из карточки заказа приходит тот самый объект, и у
     * несохранённой позиции id может не быть вовсе. По id ищем только
     * следом — так приходит строка реестра истории, у которой с позицией
     * заказа общий только идентификатор.
     */
    removePosition(position: AbstractOrderPosition): void {
        let index = this.positions.indexOf(position);

        if (index === -1) {
            if (Core.utils.number.isNumber(position.id) === true) {
                index = this.positions.findIndex(_pos => _pos.id === position.id);
            } else {
                // позицию без id по id не ищем: совпала бы первая безномерная
            }
        } else {
            // нашли по ссылке
        }

        if (index !== -1) {
            this.positions.splice(index, 1);
        } else {
            // удалять нечего
        }
    }

    // ==================== ====================

    get isMultiCurrency(): boolean {
        if (Core.utils.isEmptyArray(this.positions)) return false;
        // если валюта у заказа не установлена, то делаем вывод что заказ мультивалютный
        if (Core.utils.isNullOrUndefined(this.currency)) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Позиции, сгруппированные по валюте. Для мультивалютного заказа — разбивка,
     * из которой платёж выбирает, в какой из валют заказа он платит.
     */
    get positionsByCurrency(): Map<string, AbstractOrderPosition[]> {
        const result = new Map<string, AbstractOrderPosition[]>();

        for (const position of this.positions) {
            if (position.currencyCode === null) {
                // позиция без валюты в разбивку не попадает
            } else {
                const currencyPositions = result.get(position.currencyCode);

                if (currencyPositions === undefined) {
                    result.set(position.currencyCode, [position]);
                } else {
                    currencyPositions.push(position);
                }
            }
        }

        return result;
    }

    /**
     * Валюта учёта — локальная валюта страны партнёра. Свойством документа
     * не хранится: она одна на партнёра и от заказа не зависит.
     */
    get accountingCurrency(): Currency {
      // Я не могу использовать только this.buyer.country.localCurrency.code, там нет кода,
      // но я указываю это, потому что это правильный и чистый источник правды.
      // Есть основания полагать, что если завязаться на конфиг, то это будет справедливо некоторое время в будущем
      return Core.utils.money.getCurrencyByCode(this.buyer.country?.localCurrency?.code || Core.config.localCurrency.code)
    }

    /**
     * Полная сумма заказа в валюте сделки. Для мультивалютного заказа не
     * существует: складывать доллары с евро нельзя, шапка в этом случае
     * показывает прочерк и отсылает к разбивке по валютам.
     */
    get sumFullCalculated(): Sum | null {
      if (this.isMultiCurrency === true) {
        return null;
      }

      let result = Sum.zero();

      this.positions.forEach((position) => {
        result = result.plus(position.sumFullSeller);
      });

      return result;
    }

    /** НДС по заказу в валюте сделки. Логика та же, что у полной суммы. */
    get vatSumCalculated(): Sum | null {
      if (this.isMultiCurrency === true) {
        return null;
      }

      /** Поставщик не платит НДС — суммы нет, а не ноль.
       *  Симметрично accountingVatSumCalculated. */
      if (this.isVatPresented === false) {
        return null;
      }

      let result = Sum.zero();

      for (const position of this.positions) {
        /** Тип налогообложения у всех позиций заказа один, поэтому пустой НДС
         *  по позиции означает, что величина не посчитана: частичная сумма
         *  выглядела бы верной, будучи занижённой. */
        if (position.vatSumSeller === null) {
          return null;
        }
        else {
          result = result.plus(position.vatSumSeller);
        }
      }

      return result;
    }

    /**
     * Полная сумма заказа в валюте учёта — единственная сумма, осмысленная
     * для мультивалютного заказа: слагаемые сначала приводятся к одной
     * валюте, потом складываются.
     *
     * Геттер, а не поле: позиции меняются по одной, хранимое значение
     * пришлось бы пересчитывать вручную на каждое изменение. Курс читается
     * из реактивного кеша — осмысленный результат только в реактивном
     * контексте (шаблон, computed), зато при появлении курса значение
     * пересчитается само.
     *
     * Нет курса хотя бы по одной позиции — расчёт прекращается: частичная
     * сумма выглядела бы верной, будучи занижённой. Ошибку здесь не пишем:
     * геттер зовётся на каждую перерисовку, в том числе до прихода курсов;
     * о реальной нехватке сообщает предзагрузка в карточке.
     */
    get accountingSumFullCalculated(): Sum | null {
      let result = Sum.zero();

      // если образцы бесплатные, то считать нечего, в позиции sumFullSeller будет undefined
      if (this.isFreeSamples === true) return result;

      for (const position of this.positions) {
        CurrenciesService.requestRate(position.currencyCode, this.createdAt);

        const converted = position.sumFullSeller.convertSync(
          position.currencyCode,
          this.accountingCurrency,
          this.createdAt,
        );

        if (converted === null || converted.isEmpty() === true) {
          return null;
        } else {
          result = result.plus(converted);
        }
      }


      return result;
    }

    /** НДС по заказу в валюте учёта. Логика та же, что у полной суммы. */
    get accountingVatSumCalculated(): Sum | null {
      // Если поставщик платит, то имеет смысл рассчитывать
      if (this.isVatPresented === true) {
        let result = Sum.zero();

        // если образцы бесплатные, то считать нечего, в позиции vatSumSeller будет undefined
        if (this.isFreeSamples === true) return result;

        for (const position of this.positions) {
          CurrenciesService.requestRate(position.currencyCode, this.createdAt);

          const converted = position.vatSumSeller.convertSync(
            position.currencyCode,
            this.accountingCurrency,
            this.createdAt,
          );

          if (converted === null || converted.isEmpty() === true) {
            return null;
          } else {
            result = result.plus(converted);
          }
        }

        return result;
      }

      return null;
    }

    // ==================== Причины недоступности действий ====================

    /**
     * Причина, по которой заказ нельзя подтвердить.
     * null — подтверждение доступно.
     */
    getReasonCannotApprove(): string | null {
        const reasons: string[] = [];

        if (this.isExisting === false) reasons.push(Order.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND);
        if (this.positions?.length === 0) reasons.push(Order.REASON_UNAVAILABLE.NO_POSITIONS);
        if (this.isDraft === false) reasons.push(Order.REASON_UNAVAILABLE.ORDER_ALREADY_APPROVED);

        if (reasons.length === 0) {
            return null;
        }

        return 'Недоступно\n' + reasons.join('\n');
    }

    /**
     * Причина, по которой нельзя отправить уведомление.
     */
    getReasonCannotNotify(): string | null {
      const reasons: string[] = [];

      if (this.isExisting === false) reasons.push(Order.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND);
      if (this.positions?.length === 0) reasons.push(Order.REASON_UNAVAILABLE.NO_POSITIONS);

      if (reasons.length === 0) { return null; }

      return 'Недоступно\n' + reasons.join('\n');
    }

    /**
     * Причина, по которой нельзя массово изменить даты.
     */
    getReasonCannotSetDates(): string | null {
        if (this.positions?.length === 0) {
            return 'Недоступно\n' + Order.REASON_UNAVAILABLE.NO_POSITIONS;
        }

        return null;
    }

    /**
     * Причина, по которой заказ не может быть удалён.
     * null — удаление доступно.
     */
    getReasonCannotDelete(): string | null {
        if (this.isExisting === false) {
            return 'Недоступно\n' + Order.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
        }

        return null;
    }

    /**
     * Причина, по которой недоступны файлы.
     * Условия совпадают с удалением — нужен сохранённый заказ.
     */
    getReasonCannotAccessFiles(): string | null {
        return this.getReasonCannotDelete();
    }

    /**
     * Причина, по которой нельзя открыть ленту.
     */
    getReasonCannotAccessFeed(): string | null {
        return this.getReasonCannotDelete();
    }

    /**
     * Причина, по которой нельзя копировать заказ.
     */
    getReasonCannotCopy(): string | null {
        return this.getReasonCannotDelete();
    }
}

export default Order;